import type { FieldNode, ValidationConfig } from '../validationEngine/types';
import { customerAddressValidationConfig } from './addressValidationConfig';
import { withoutRequiredRulesFromGroup } from './validationConfigRuleUtils';
import { transformValidationConfig } from '../validationEngine/transformValidationConfig';

export const validateNumberRange = (value: unknown, min: number, max: number, integerOnly = false): boolean => {
    if (value === undefined || value === null || value === '') return true;
    const num = Number(value);
    if (Number.isNaN(num)) return false;
    if (integerOnly && !Number.isInteger(num)) return false;
    return num >= min && num <= max;
};

const employerAddressFields = withoutRequiredRulesFromGroup(customerAddressValidationConfig);
const streetAddress = employerAddressFields.streetAddress as FieldNode;
const city = employerAddressFields.city as FieldNode;
const state = employerAddressFields.state as FieldNode;
const zip = employerAddressFields.zip as FieldNode;

const employerAddressConfig = transformValidationConfig(
    {
        streetAddress,
        city,
        state,
        zip
    } as ValidationConfig,
    { fieldLabels: { streetAddress: 'Employer Address' } }
);

export const employmentValidationConfig: ValidationConfig = {
    $group: {
        employerName: {
            rules: [
                {
                    type: 'MAX_LENGTH',
                    params: { max: 30 },
                    messages: 'Employer Name must be under 30 characters.'
                }
            ]
        },
        occupation: {
            rules: [
                {
                    type: 'MAX_LENGTH',
                    params: { max: 26 },
                    messages: 'Occupation must be under 26 characters.'
                }
            ]
        },
        totalYearsEmployed: {
            rules: [
                {
                    type: 'CUSTOM',
                    params: {
                        evaluator: (value: unknown) => {
                            return validateNumberRange(value, 0, 99, true);
                        }
                    },
                    messages: 'Yrs. at Employer must be between 0 and 99.'
                }
            ]
        },
        totalMonthsEmployed: {
            rules: [
                {
                    type: 'CUSTOM',
                    params: {
                        evaluator: (value: unknown) => {
                            return validateNumberRange(value, 0, 11, true);
                        }
                    },
                    messages: 'Mos. at Employer must be between 0 and 11.'
                }
            ]
        },
        address: {
            $group: employerAddressConfig
        }
    }
};

export const customerHousingAndEmploymentValidationConfig: ValidationConfig = {
    financialInfo: {
        $group: {
            income: {
                rules: [
                    {
                        type: 'CUSTOM',
                        params: {
                            evaluator: (value: unknown) => {
                                return validateNumberRange(value, 0, 999999);
                            }
                        },
                        messages: 'Expected Income must be between 0 and 999,999.'
                    }
                ]
            },
            otherIncomeSource: {
                rules: [
                    {
                        type: 'MAX_LENGTH',
                        params: { max: 60 },
                        messages: 'Additional Source of Income must be under 60 characters.'
                    }
                ]
            },
            otherIncome: {
                rules: [
                    {
                        type: 'CUSTOM',
                        params: {
                            evaluator: (value: unknown) => {
                                return validateNumberRange(value, 0, 999999);
                            }
                        },
                        messages: 'Additional Monthly Income must be between 0 and 999,999.'
                    }
                ]
            }
        }
    },
    housingInfo: {
        $group: {
            mortgageOrRentAmount: {
                rules: [
                    {
                        type: 'CUSTOM',
                        params: {
                            evaluator: (value: unknown) => {
                                if (value === undefined || value === null || value === '') return true;
                                const num = Number(value);
                                return !isNaN(num) && num >= 0 && num <= 999999;
                            }
                        },
                        messages: 'Mortgage/Rent amount must be between 0 and 999,999.'
                    }
                ]
            }
        }
    },
    yearsAtCurrentAddress: {
        rules: [
            {
                type: 'CUSTOM',
                params: {
                    evaluator: (value: unknown) => {
                        if (value === undefined || value === null || value === '') return true;
                        const num = Number(value);
                        return !isNaN(num) && num >= 0 && num <= 99;
                    }
                },
                messages: 'Yrs. at Address must be between 0 and 99.'
            }
        ]
    },
    monthsAtCurrentAddress: {
        rules: [
            {
                type: 'CUSTOM',
                params: {
                    evaluator: (value: unknown) => {
                        if (value === undefined || value === null || value === '') return true;
                        const num = Number(value);
                        return !isNaN(num) && num >= 0 && num <= 11;
                    }
                },
                messages: 'Mos. at Address must be between 0 and 11.'
            }
        ]
    },

    previousAddress: {
        $group: withoutRequiredRulesFromGroup(customerAddressValidationConfig)
    },
    yearsAtPreviousAddress: {
        rules: [
            {
                type: 'CUSTOM',
                params: {
                    evaluator: (value: unknown) => {
                        if (value === undefined || value === null || value === '') return true;
                        const num = Number(value);
                        return Number.isInteger(num) && num >= 0 && num <= 99;
                    }
                },
                messages: 'Years at previous address must be a whole number between 0 and 99.'
            }
        ]
    },
    monthsAtPreviousAddress: {
        rules: [
            {
                type: 'CUSTOM',
                params: {
                    evaluator: (value: unknown) => {
                        if (value === undefined || value === null || value === '') return true;
                        const num = Number(value);
                        return Number.isInteger(num) && num >= 0 && num <= 11;
                    }
                },
                messages: 'Months at previous address must be a whole number between 0 and 11.'
            }
        ]
    },
    currentEmployment: employmentValidationConfig,
    previousEmployment: employmentValidationConfig
};
